import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
import Controller, { inject as controller } from '@ember/controller';
import { isEmpty, isNone } from '@ember/utils';
import CustomerModel from 'cio/models/customer';
import SettingsService from 'cio/services/settings';
import PeopleListController from '../controller';
import { ATTRIBUTES_TO_IGNORE_FOR_TIMESTAMPS } from 'cio/utils/attributes';
import { tracked } from '@glimmer/tracking';
import { FlyMouseEvent } from '@customerio/fly-components/types/fly-components/events';
import AuthService from 'cio/services/auth';
import rangeSelectionHelper from 'cio/utils/range-selection-helper';

export default class IndexController extends Controller {
  queryParams = ['email', 'filters', 'page', 'source'];
  email = '';
  filters: string | null = null;
  page = 1;
  source: string | null = null;
  attributesToIgnoreForTimestamps = ATTRIBUTES_TO_IGNORE_FOR_TIMESTAMPS;

  @service settings!: SettingsService;
  @service auth!: AuthService;

  declare model: CustomerModel[];

  @controller('people.list') listController!: PeopleListController;
  @readOnly('listController.totalColumns') totalColumns!: number;

  selectedPeople: CustomerModel[] = [];
  _allSelected = false;
  totalRecs!: number;

  @computed('filters', 'email')
  get hasFilters() {
    return !isNone(this.filters) || !isEmpty(this.email);
  }

  @computed('_allSelected', 'selectedPeople.[]', 'totalRecs')
  get allSelected() {
    return (this.totalRecs && this.totalRecs === this.selectedPeople.length) || this._allSelected;
  }

  set allSelected(value) {
    let selectedPeople = value ? this.get('model').slice() : [];

    this.setProperties({
      selectedPeople,
      _allSelected: value,
    });
  }

  resetPagination() {
    this.set('page', 1);
  }

  @tracked shiftSelectStart: number | null = null;
  @tracked shiftSelectEnd: number | null = null;

  @action
  personChecked(
    person: CustomerModel,
    index: number,
    checked: boolean,
    metadata: { event: FlyMouseEvent<HTMLInputElement> },
  ) {
    let { tempShiftSelectEnd, tempShiftSelectStart, allSelected } = rangeSelectionHelper<CustomerModel>({
      models: this.model,
      singleModel: person,
      selectedItems: this.selectedPeople,
      index,
      checked,
      totalRecs: this.totalRecs,
      metadata,
      shiftSelectStart: this.shiftSelectStart,
      shiftSelectEnd: this.shiftSelectEnd,
    });

    this.shiftSelectStart = tempShiftSelectStart;
    this.shiftSelectEnd = tempShiftSelectEnd;
    this.set('_allSelected', allSelected);
  }

  @action
  selectAllPeopleOnPage(checked: boolean) {
    if (checked) {
      this.set('selectedPeople', this.model.slice());
      this.set('_allSelected', this.selectedPeople.length === this.totalRecs);
    } else {
      this.setProperties({
        selectedPeople: [],
        _allSelected: false,
      });
    }
  }

  @action
  clearSelection() {
    this.setProperties({
      selectedPeople: [],
      _allSelected: false,
    });
  }
}

declare module '@ember/controller' {
  interface Registry {
    'people.list.index': IndexController;
  }
}
